import { PieChart as PieChartIcon } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { GENRE_COLORS } from '../../utils/analyticsUtils';

interface GenreData {
  genre: string;
  count: number;
  percentage: number;
  color: string;
}

interface Props {
  data: GenreData[];
}

export default function GenreBreakdown({ data }: Props) {
  if (data.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <PieChartIcon className="w-5 h-5 text-primary" />
          Genre Distribution
        </h3>
        <div className="h-48 flex items-center justify-center text-gray-500">
          <p>No genre data available</p>
        </div>
      </div>
    );
  }

  const chartData = data.map(d => ({
    name: d.genre,
    value: d.count,
    color: d.color,
  }));

  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <PieChartIcon className="w-5 h-5 text-primary" />
        Genre Distribution
      </h3>
      <div className="flex gap-4 items-center">
        <ResponsiveContainer width="50%" height={200}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={40}
              outerRadius={80}
              paddingAngle={2}
              dataKey="value"
            >
              {chartData.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: '#1F2937',
                border: 'none',
                borderRadius: '8px',
                color: '#F3F4F6',
                fontSize: 12,
              }}
              formatter={(value?: number, name?: string) => [`${value ?? 0} songs`, name ?? '']}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="flex-1 space-y-2 max-h-48 overflow-y-auto">
          {data.map((g) => (
            <div key={g.genre} className="flex items-center gap-2 text-sm">
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: GENRE_COLORS[g.genre] || GENRE_COLORS['Other'] }}
              />
              <span className="flex-1 text-gray-900 dark:text-white truncate">
                {g.genre}
              </span>
              <span className="text-gray-500 dark:text-gray-400 tabular-nums">
                {g.count} ({g.percentage}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
